import { useEffect, useRef } from 'react';

/**
 * HubSpot's meetings embed loader. Already requested once per page by
 * `templates/front/common/hubspot.html.twig`; re-requested here from the browser cache.
 */
const EMBED_SCRIPT = 'https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js';

interface Props {
  /** The meeting page to embed, `?embed=true` included, as HubSpot hands it out. */
  src: string
}

/**
 * A HubSpot meetings booking calendar, embedded in the page.
 *
 * The snippet HubSpot gives out is a placeholder div followed by a `<script>`. That shape cannot be
 * pasted into a component: React does not execute a script tag it renders, and the copy of the
 * loader the layout already includes has run long before this component mounts — it scans the
 * document for `.meetings-iframe-container` at load time, finds nothing, and never looks again.
 *
 * So the loader is re-requested on mount, which makes it scan once more, this time with the
 * placeholder present. It is a repeat request for a script already in cache, and it is the only
 * moment at which the container it needs exists.
 *
 * Nothing runs during server-side rendering — effects do not — so the page ships the empty
 * placeholder and the calendar appears on hydration. That is also why the placeholder must keep a
 * height of its own: without one the section would collapse and jump.
 */
export default function HubspotMeetings({ src }: Props) {
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = container.current;

    if (node === null) {
      return undefined;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = EMBED_SCRIPT;
    node.appendChild(script);

    // The iframe HubSpot injects lives inside the placeholder, which React unmounts with the rest of
    // the subtree; only the script tag added here is ours to take back.
    return () => script.remove();
  }, [src]);

  return (
    <div className="hubspot-meetings" ref={container}>
      {/* Class and data attribute are HubSpot's contract: the loader looks the container up by them. */}
      <div className="meetings-iframe-container" data-src={src} />
    </div>
  );
}
